import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiUser, FiLock } from "react-icons/fi";
import * as Yup from "yup";
import { AxiosError } from "axios";
import { useReducedMotion } from "framer-motion";

import { Form } from "@unform/web";
import { FormHandles } from "@unform/core";
import getValidationErrors from "../../../utils/getValidationErrors";

import { useAuth } from "../../../hooks/auth";
import { useToast } from "../../../hooks/toast";
import { useHistory } from "react-router-dom";

import Input from "../../../components/Atoms/Input";
import Loading, {
  SPINNER_ON_GOLD,
} from "../../../components/Atoms/Loading";

import {
  Container,
  ContentWrapper,
  LoginWrapper,
  FormWrapper,
  StyledButton,
  LogoWrapper,
  LogoButton,
} from "./styles";
import { useInitialRoute } from "../../../hooks/InitialRouteContext";
import { tokens } from "../../../design/tokens";

const ENTRANCE_DURATION = parseFloat(tokens.motion.slow);


// Banners de cliente: apenas ~5 dos 24 subdominios usam um, e alguns PNGs
// pesam multiplos MB (ver PRODUCT.md). Cada um so entra no bundle quando o
// subdominio atual realmente precisa dele, em vez de todos os 24 pagarem
// o peso dos 5.
const BANNER_LOADERS: Record<string, () => Promise<{ default: string }>> = {
  raizdaquestao: () => import("../../../assets/images/RaízdaQuestão.png"),
  escolaimpulse: () => import("../../../assets/images/ImpulseLogin.png"),
  unipsaojoao: () => import("../../../assets/images/UnipSaoJoao.png"),
  igrejabase: () => import("../../../assets/images/igrejabaseBanner.png"),
  sbpv: () => import("../../../assets/images/sbpvBanner.png"),
};

const PAGE_TITLES: Record<string, string> = {
  setepebetap: "SETEPEB ETAP",
  setepeb: "SETEPEB",
  setepebarapiraca: "SETEPEB ARAPIRACA",
  setepebcaruaru: "SETEPEB CARUARU",
};

interface DataFormInfo {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const [isLogging, setIsLogging] = useState(false);
  const [bannerSrc, setBannerSrc] = useState<string | null>(null);

  // A entrada e enquadramento, nao informacao: para quem pediu menos
  // movimento ela vira um fade sem deslocamento, em vez de sumir.
  const reduceMotion = useReducedMotion();

  const formRef = useRef<FormHandles>(null);

  const { signIn } = useAuth();
  const { addToast } = useToast();
  const { push } = useHistory();

  const handleLogin = useCallback(
    async (data: DataFormInfo) => {
      setIsLogging(true);
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          username: Yup.string().required("Usuário obrigatório!"),
          password: Yup.string().required("Senha obrigatória!"),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const targetUrl = await signIn({
          username: data.username,
          password: data.password,
        });

        setIsLogging(false);

        push(targetUrl);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
          setIsLogging(false);
        } else {
          const axiosErr = err as AxiosError<string>;
          const backendMessage =
            typeof axiosErr?.response?.data === "string" &&
            axiosErr.response.data.trim() !== ""
              ? axiosErr.response.data
              : undefined;

          // Um erro sem `response` pode ser rede caída OU uma resposta de
          // erro do backend sem header CORS (o navegador bloqueia o corpo
          // nesse caso, e o JS nao consegue distinguir os dois). Por isso a
          // mensagem nao afirma qual dos dois aconteceu quando nao ha texto
          // do backend para confirmar.
          addToast({
            type: "error",
            title: "Erro na autenticação :(",
            description:
              backendMessage ??
              "Não foi possível entrar. Confira seu usuário e senha, ou tente novamente em instantes.",
          });
          setIsLogging(false);
        }
      }
    },
    [addToast, signIn, push]
  );

  const { subdomainUrlImg } = useInitialRoute();

  let urlAtual =
    process.env.NODE_ENV === "development"
      ? `${process.env.REACT_APP_WHITELABEL}`
      : window.location.href;
  let splitUrlProd = urlAtual.split(".");
  let subdomain = splitUrlProd[0].includes("www")
    ? ["", splitUrlProd[1]]
    : splitUrlProd[0].split("//");

  // Mesmos quatro subdominios e mesmo fallback da escada ternaria anterior —
  // so extraido do JSX. Continua sendo configuracao hardcoded: cliente novo
  // ainda exige editar este arquivo (ver PRODUCT.md, "onboarding e
  // configuracao, nao engenharia").
  const pageTitle = PAGE_TITLES[subdomain[1]] ?? "LOGIN";

  useEffect(() => {
    const loadBanner = BANNER_LOADERS[subdomain[1]];

    if (loadBanner) {
      loadBanner().then((mod) => setBannerSrc(mod.default));
    } else {
      setBannerSrc(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdomain[1]]);

  return (
    <Container>
      <ContentWrapper>
        <LoginWrapper
          initial={{ opacity: 0, y: reduceMotion ? 0 : 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: ENTRANCE_DURATION }}
        >
          <LogoButton type="button" onClick={() => push("./")}>
            <img src={subdomainUrlImg} alt={`${pageTitle} — ir para o início`} />
          </LogoButton>
          <Form ref={formRef} onSubmit={handleLogin} className="formStyle">
            <FormWrapper>
              <h1>{pageTitle}</h1>
              <Input
                name="username"
                icon={FiUser}
                placeholder="Digite seu e-mail"
                autoComplete="username"
                customWidth="100%"
                enabled={!isLogging}
              />
              <Input
                name="password"
                icon={FiLock}
                type="password"
                placeholder="Sua senha"
                autoComplete="current-password"
                customWidth="100%"
                enabled={!isLogging}
              />
              <button
                className="forgot"
                type="button"
                onClick={() => push("/password/forgot")}
              >
                Esqueci minha senha
              </button>
              <StyledButton
                type="submit"
                enabled={!isLogging}
                /* Durante o envio o rotulo "ENTRAR" da lugar ao spinner e o
                   botao ficaria sem nome acessivel — o formulario emudecia
                   entre o clique e a resposta. */
                aria-label={isLogging ? "Entrando…" : undefined}
                aria-busy={isLogging}
              >
                {isLogging ? (
                  <Loading size={2} customColors={SPINNER_ON_GOLD} />
                ) : (
                  "ENTRAR"
                )}
              </StyledButton>

              <StyledButton
                onClick={() => push("/signup")}
                enabled={!isLogging}
                contrast
              >
                Crie uma conta
              </StyledButton>
            </FormWrapper>
          </Form>
        </LoginWrapper>
        {bannerSrc && (
          <LogoWrapper
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: ENTRANCE_DURATION,
              delay: reduceMotion ? 0 : 0.1,
            }}
          >
            <img src={bannerSrc} alt="" />
          </LogoWrapper>
        )}
      </ContentWrapper>
    </Container>
  );
};

export default Login;
